import type { CSSProperties } from 'react'

import { useWebinarDesignQuery } from '../Designs/blocs/useWebinarDesign'
import { useFeedsInFloor, useFloorState } from '../firebase/hooks/useFloor'
import {
  getEditingFeedIdx,
  getEditingMix,
  updateEditingMix,
  useEditingMixState,
  useMixes,
} from '../firebase/hooks/useMixes'
import type { Feed, Mix, IndexedElement, Layout } from '../firebase/model/mix'
import { copy } from '../global/utils/valtio'

type Position = {
  x: number
  y: number
  w: number
  h: number
  z?: number
  zoom?: number
  centerX?: number
  centerY?: number
}
type MediaType = 'tray' | 'camera' | 'screen' | 'mobile'

const useSideBySideFeed = (type: MediaType, id: number): Feed | undefined => {
  const { data: webinarDesign } = useWebinarDesignQuery()

  const feedsInFloor = useFeedsInFloor()
  const layout = webinarDesign?.designData['standard-layouts']['side-by-side']

  if (!feedsInFloor.some((f) => f.id === id && f.type === type))
    return undefined

  if (!layout) return undefined

  switch (type) {
    case 'camera':
    case 'mobile': {
      const index = layout.elements.findIndex((el) => el.type === 'camera')
      if (index === -1) return undefined
      return {
        ...layout.elements[index],
        index,
        type: 'camera',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: COVER_ZOOM },
      }
    }
    case 'screen':
    case 'tray': {
      const index = layout.elements.findIndex((el) => el.type === 'media')
      if (index === -1) return undefined

      return {
        ...layout.elements[index],
        index,
        type: 'camera',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: CONTAIN_ZOOM },
      }
    }
    default:
      return undefined
  }
}

const useSoloFeed = (type: MediaType, id: number): Feed | undefined => {
  const { data: webinarDesign } = useWebinarDesignQuery()

  const feedsInFloor = useFeedsInFloor()
  const layouts = webinarDesign?.designData['standard-layouts']

  if (!feedsInFloor.some((f) => f.id === id && f.type === type))
    return undefined

  switch (type) {
    case 'camera':
    case 'mobile': {
      const feed = layouts?.['speaker-full'].elements[0]
      if (!feed) return undefined
      return {
        ...feed,
        index: 1,
        type: 'camera',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: COVER_ZOOM },
      }
    }
    case 'screen':
    case 'tray': {
      const feed = layouts?.['media-full'].elements[0]
      if (!feed) return undefined
      return {
        ...feed,
        type: 'media',
        index: 1,
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: CONTAIN_ZOOM },
      }
    }
    default:
      return undefined
  }
}

const usePanelLayout = (camerasCount: number, hasMedia: boolean) => {
  const { data: webinarDesign } = useWebinarDesignQuery()

  const layoutType = hasMedia ? 'media' : 'nomedia'
  const settings = webinarDesign?.designData['standard-layouts'].panel.settings
  const elements =
    webinarDesign?.designData['standard-layouts'].panel.configurations[
      `${layoutType}-${camerasCount}` as keyof NonNullable<
        typeof webinarDesign
      >['designData']['standard-layouts']['panel']['configurations']
    ] ?? []

  return {
    settings,
    elements,
  }
}

const usePanelFeed = (type: MediaType, id: number): Feed | undefined => {
  const { data: webinarDesign } = useWebinarDesignQuery()

  const feedsInFloor = useFeedsInFloor()
  const camerasInFloor = feedsInFloor.filter(
    (f) => f.type === 'camera' || f.type === 'mobile'
  )

  const hasMedia = feedsInFloor.some(
    (f) => f.type === 'screen' || f.type === 'tray'
  )
  const layoutType = hasMedia ? 'media' : 'nomedia'
  const layout =
    webinarDesign?.designData['standard-layouts'].panel.configurations[
      `${layoutType}-${camerasInFloor.length}` as keyof NonNullable<
        typeof webinarDesign
      >['designData']['standard-layouts']['panel']['configurations']
    ]

  if (type === 'tray' || type === 'screen') {
    if (layout && feedsInFloor.find((f) => f.type === type)) {
      const index = layout.findIndex((l) => l.type === 'media')
      if (index === -1) return undefined

      return {
        ...layout[index],
        index,
        type: 'media',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: CONTAIN_ZOOM },
      }
    }
    return undefined
  }

  const index = camerasInFloor.findIndex((f) => f.type === type && f.id === id)

  const feed = layout?.filter((l) => l.type === 'camera')[index]
  if (!feed) return undefined

  return {
    ...feed,
    index,
    type: 'camera',
    media: { type, id },
    crop: { centerX: 0, centerY: 0, zoom: COVER_ZOOM },
  }
}

const useCustomFeed = (type: MediaType, id: number) => {
  const mixes = useMixes()

  const floor = useFloorState()

  if (!floor || floor.layout !== 'custom') return undefined

  const mix = mixes.find((m) => m.id === floor.mixId)
  if (!mix) return undefined

  return mix.feeds.find(
    (f) =>
      f.media?.type === type &&
      (f.media.type === 'tray' || ('id' in f.media && f.media.id === id))
  )
}

const useMixEditingFeed = (type: MediaType, id: number) => {
  const editingMix = useEditingMixState()

  if (!editingMix?.feeds) return undefined

  return editingMix.feeds.find(
    (f) =>
      f.media?.type === type &&
      (f.media.type === 'tray' || ('id' in f.media && f.media.id === id))
  )
}

const useFeed = (type: MediaType, id: number) => {
  const floor = useFloorState()
  const editingMix = useEditingMixState()
  const editingFeed = useMixEditingFeed(type, id)
  const autoFeed = usePanelFeed(type, id)
  const customFeed = useCustomFeed(type, id)
  const soloFeed = useSoloFeed(type, id)
  const sideBySideFeed = useSideBySideFeed(type, id)

  if (editingMix) return editingFeed

  switch (floor?.layout) {
    case 'custom':
      return customFeed
    case 'panel':
      return autoFeed
    case 'side-by-side':
      return sideBySideFeed
    case 'tray':
    case 'solo':
      return soloFeed
  }
  return undefined
}

const updateFeedPosition = (updater: (feed: Feed) => Feed | undefined) => {
  const mix = getEditingMix()
  const feedIdx = getEditingFeedIdx()
  if (feedIdx === null) return

  const newMix = copy(mix) as Mix

  const newFeed = newMix.feeds[feedIdx]
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!newFeed) return
  const updatedFeed = updater(newFeed)
  if (!updatedFeed) return
  newMix.feeds[feedIdx] = updatedFeed
  updateEditingMix(newMix)
}

const useLayoutSettings = (): Layout['settings'] | undefined => {
  const floor = useFloorState()
  const editingMix = useEditingMixState()
  const mixes = useMixes()
  const { data: webinarDesign } = useWebinarDesignQuery()

  if (editingMix) return editingMix.settings

  switch (floor?.layout) {
    case 'custom':
      return mixes.find((m) => m.id === floor.mixId)?.settings
    case 'panel':
      return webinarDesign?.designData['standard-layouts'].panel.settings
    case 'side-by-side':
      return webinarDesign?.designData['standard-layouts']['side-by-side']
        .settings
    case 'tray':
      return webinarDesign?.designData['standard-layouts']['media-full']
        .settings
    case 'solo':
      if (floor.identifier === 'screenshare')
        return webinarDesign?.designData['standard-layouts']['media-full']
          .settings

      return webinarDesign?.designData['standard-layouts']['speaker-full']
        .settings
  }

  return undefined
}

const COVER_ZOOM = 30
const CONTAIN_ZOOM = 0

const posToStyle = (
  feed: Feed | IndexedElement | undefined,
  options?: {
    withAnchor?: boolean
  }
) => {
  if (!feed?.position) {
    return {
      // opacity: 0,
      top: '33%',
      left: '135%',
      height: '33%',
      width: '25%',
    } as CSSProperties
  }

  const zIndex = feed.index
  const anchorName = options?.withAnchor ? `--slot-${feed.index}` : undefined

  const aspectRatio = ((feed.position.w / feed.position.h) * 16) / 9
  const absRatio = aspectRatio < 1 ? 1 / aspectRatio : aspectRatio

  return {
    left: `${feed.position.x}%`,
    top: `${feed.position.y}%`,
    width: `${feed.position.w}%`,
    height: `${feed.position.h}%`,
    zIndex,
    anchorName,
    containerType: 'size',
    '--width': feed.position.w,
    '--aspect-ratio': aspectRatio,
    '--abs-ratio': absRatio,

    '--skew': `${feed.skew ?? 0}deg`,

    '--border-radius-top-left': `${feed.radius?.topLeft ?? 0}em`,
    '--border-radius-top-right': `${feed.radius?.topRight ?? 0}em`,
    '--border-radius-bottom-left': `${feed.radius?.bottomLeft ?? 0}em`,
    '--border-radius-bottom-right': `${feed.radius?.bottomRight ?? 0}em`,

    '--outline-width': `${feed.outline?.width ?? 0}em`,
    // TODO: add support for brand token
    '--outline-color': feed.outline?.color ?? 'transparent',

    ...('crop' in feed
      ? {
          '--zoomOut':
            feed.crop.zoom < COVER_ZOOM ? 1 - feed.crop.zoom / COVER_ZOOM : 0,
          '--zoomIn':
            feed.crop.zoom > COVER_ZOOM
              ? (feed.crop.zoom - COVER_ZOOM) / (100 - COVER_ZOOM)
              : 0,
          '--centerX': feed.crop.centerX,
          '--centerY': feed.crop.centerY,
        }
      : {}),
  } as CSSProperties
}

export {
  useFeed,
  usePanelLayout,
  useLayoutSettings,
  updateFeedPosition,
  posToStyle,
  type MediaType,
  type Position,
}
